import { makeStyles } from '@map/theme';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import Typography from '@mui/material/Typography';
import ChevronDownIcon from '@ui/Icon/library/ChevronDownIcon';
import ChevronUpIcon from '@ui/Icon/library/ChevronUpIcon';
import NavButton from '@ui/NavButton';
import { SyntheticEvent, forwardRef, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CookieSectionType, cookieSections } from './CookieBanner';
import CookieDetail from './CookieDetail';
import CookieFormSetting from './CookieFormSetting';
import usedCookies from './usedCookies.json';

const useStyles = makeStyles(({ spacing, palette }) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacing(2),
    marginBottom: spacing(3),
    borderBottom: `1px solid ${palette.neutrals[500]}`,
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    '& >:first-child': {
      flexGrow: 1,
    },
  },
  content: {
    maxHeight: '60vh',
    margin: spacing(0, -6),
    overflow: 'auto',
    padding: spacing(0, 6),
    '& p': {
      paddingLeft: spacing(2),
      marginBottom: spacing(3),
      '& button': {
        display: 'inline',
        padding: 0,
        margin: 0,
        width: 'auto',
      },
    },
  },
  cookieSection: {
    '& .MuiAccordion-root': {},
    '& .MuiAccordionSummary-content': {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      '& p': {
        margin: 0,
        flexGrow: 1,
      },
      '& > :last-child': {
        alignSelf: 'flex-end',
      },
    },
  },
  closeButton: {
    marginRight: spacing(-2),
  },
}));

type CookieFormPropsType = {
  onClose: () => void;
  onSave: () => void;
  onChange: (section: CookieSectionType) => void;
};

export type CookieSectionPanel = CookieSectionType | false;

const localizations = {
  essential_cookies: {
    title: <FormattedMessage id="cookie_banner.cookie_form.essential_cookies_title" />,
    description: <FormattedMessage id="cookie_banner.cookie_form.essential_cookies_description" />,
  },
  analytics_cookies: {
    title: <FormattedMessage id="cookie_banner.cookie_form.analytics_cookies_title" />,
    description: <FormattedMessage id="cookie_banner.cookie_form.analytics_cookies_description" />,
  },
};

const CookieForm: FFC<CookieFormPropsType> = ({ onClose, onChange }, ref) => {
  const { classes } = useStyles();

  const [expanded, setExpanded] = useState<CookieSectionPanel>(false);

  const handleChange = useCallback(
    (section: CookieSectionPanel) => (_e: SyntheticEvent<Element, Event>) =>
      setExpanded((currentPanel) => (currentPanel === section ? false : section)),
    [],
  );

  const handlePreferenceChange = useCallback(
    (section: CookieSectionType) => () => onChange(section),
    [onChange],
  );

  return (
    <div ref={ref}>
      <div className={classes.header}>
        <Typography variant="h4">
          <FormattedMessage id="cookie_banner.cookie_form.custmize_consent" />
        </Typography>
        <NavButton className={classes.closeButton} which="appbar-close" onClick={onClose} />
      </div>

      <div className={classes.content}>
        {cookieSections.map((section) => (
          <Accordion
            expanded={expanded === section}
            onChange={handleChange(section)}
            elevation={0}
            key={section}
            className={classes.cookieSection}
            disableGutters
            square
          >
            <div className={classes.summary}>
              <div>
                <AccordionSummary id={`${section}-header`} aria-controls={`${section}-content`}>
                  {expanded === section ? (
                    <ChevronUpIcon color="primary" fontSize="small" />
                  ) : (
                    <ChevronDownIcon color="primary" fontSize="small" />
                  )}
                  <Typography>{localizations[section].title}</Typography>
                </AccordionSummary>
              </div>
              <CookieFormSetting
                variant={usedCookies[section].status}
                onClick={handlePreferenceChange(section)}
              />
            </div>

            <AccordionDetails>
              <Typography variant="body1">{localizations[section].description}</Typography>

              {usedCookies[section].details.map(({ name, duration, description }) => (
                <CookieDetail key={name} {...{ name, duration, description }} />
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
};

export default forwardRef(CookieForm);
